import { Download, Landscape } from "@mui/icons-material";
import { Button, Menu, MenuItem } from "@mui/material";
import jsPDF from "jspdf";
import { useState } from "react";
import { toJpeg, toPng } from "html-to-image";
import bgPortrait from "../../../../assets/images/EptaBloom_portrait.jpg";
import bgLandscape from "../../../../assets/images/EptaBloom_landscape.jpg";
import { useTemplateController } from "context";
import { setIsExporting } from "context";

const PDFExportButton = ({
  chartRef,
  onFitChart,
  period,
  fileName,
  setFullLoading,
}) => {
  const [, dispatch] = useTemplateController();
  const [anchorEl, setAnchorEl] = useState(null);
  const pixelRatio = 6;

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDownloadPdf = async (chartRef, shouldFit = false, onFitChart) => {
    if (!chartRef) return;

    try {
      // setIsExporting(dispatch, true);
      setFullLoading(true);
      if (onFitChart && shouldFit) {
        await onFitChart();
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const container = chartRef.current;

      const dataUrl = await toPng(container, {
        cacheBust: true,
        bgColor: "transparent",
        skipFonts: true,
        pixelRatio,
      });

      const graphImg = new Image();
      graphImg.src = dataUrl;

      graphImg.onload = () => {
        const isLandscape = graphImg.width > graphImg.height;
        const a4Base = isLandscape
          ? { width: 3508, height: 2480, bg: bgLandscape }
          : { width: 2480, height: 3508, bg: bgPortrait };
        const a4 = {
          width: a4Base.width * (pixelRatio / 2),
          height: a4Base.height * (pixelRatio / 2),
          bg: isLandscape ? bgLandscape : bgPortrait,
        };

        const canvas = document.createElement("canvas");
        canvas.width = a4.width;
        canvas.height = a4.height;
        const ctx = canvas.getContext("2d");

        const bg = new Image();
        bg.src = a4.bg;
        bg.onload = async() => {
          ctx.drawImage(bg, 0, 0, a4.width, a4.height);

          //Area utilizzabile per il grafico con margini appropriati
          const margins = {
            top: a4.height * 0.03, //3%
            bottom: a4.height * 0.03, //3%
            left: a4.width * 0.03, //3%
            right: a4.width * 0.03, //3%
          };

          // Area utilizzabile per il grafico
          const usableArea = {
            x: margins.left,
            y: margins.top,
            width: a4.width - margins.left - margins.right,
            height: a4.height - margins.top - margins.bottom,
          };

          // Calcolo il ratio per riempire meglio lo spazio disponibile
          const scaleX = usableArea.width / graphImg.width;
          const scaleY = usableArea.height / graphImg.height;

          // Us0 il ratio più grande tra i due per un'immagine più grande
          // ma limit0 a un massimo per evitare pixelizzazione
          const baseRatio = Math.min(scaleX, scaleY);
          const ratio = baseRatio * 1.05; //ingrandimento del 5%

          const graphW = graphImg.width * ratio;
          const graphH = graphImg.height * ratio;

          const finalGraphW = Math.min(graphW, usableArea.width);
          const finalGraphH = Math.min(graphH, usableArea.height);

          // Centr0 nell'area utilizzabile
          const offsetX = usableArea.x + (usableArea.width - finalGraphW) / 2;
          const offsetY = usableArea.y + (usableArea.height - finalGraphH) / 2;

          // Disegn0 il grafico
          ctx.drawImage(graphImg, offsetX, offsetY, finalGraphW, finalGraphH);

          const finalDataUrl = canvas.toDataURL("image/jpeg", 0.95); // Qualità più alta

          const pdf = new jsPDF({
            orientation: isLandscape ? "landscape" : "portrait",
            unit: "px",
            format: [a4.width, a4.height],
          });

          pdf.addImage(
            finalDataUrl,
            "JPEG",
            0,
            0,
            a4.width,
            a4.height,
            undefined,
            "NONE"
          );

          const timestamp = new Date().toLocaleString(navigator.language, {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });

          pdf.setFontSize(105);
          pdf.setTextColor("#ffffff");
          pdf.text(`${period.format("DD/MM/YYYY")}`, 20, 100);
          pdf.save(`${fileName}_${period.format("DD/MM/YYYY")}.pdf`);
        };
      };
    } catch (error) {
      console.error("errore nel generare il pdf", error);
    } finally {
      setFullLoading(false);
    }
  };

  const handleDirectDownload = () => {
    handleDownloadPdf(chartRef, false, onFitChart);
    handleCloseMenu();
  };

  const handleFitAndDownload = () => {
    handleDownloadPdf(chartRef, true, onFitChart);
    handleCloseMenu();
  };

  return (
    <>
      <Button
        variant="contained"
        color="white"
        className="btnDownload"
        onClick={handleOpenMenu}
        sx={{
          backgroundColor: "#ffffff",
          border: "1px solid #00449e",
          borderColor: "#00449e",
          cursor: "pointer",
          borderRadius: "0.5rem",
          color: "#00449e",
          "&:hover": {
            backgroundColor: "#eaf1fb",
          },
          "&:focus:not(:hover)": {
            backgroundColor: "#ffffff",
            boxShadow: "none",
          },
        }}
      >
        <Download size="1rem" sx={{ color: "#00449e" }} />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        sx={{
          "& .MuiPaper-root ": {
            padding: "0.5rem",
          },
          left: "-50px",
          "& .MuiMenuItem-root": {
            padding: "0.5rem",
            minWidth: "5rem",
          },
        }}
      >
        <MenuItem onClick={handleDirectDownload}>Direct download</MenuItem>
        <MenuItem onClick={handleFitAndDownload}>Fit and download</MenuItem>
      </Menu>
    </>
  );
};

export default PDFExportButton;
